import React, { useState } from 'react';
import { Select, Checkbox, Button, Space, message, Card, Radio } from 'antd';
import { DeleteOutlined, PlusOutlined, ShoppingCartOutlined } from '@ant-design/icons';

const { Option } = Select;

// Helper to compute discount
const computeDiscountedPrice = (price, discountValue, discountType, maxDiscountAmount) => {
  const originalPrice = parseFloat(price);
  if (isNaN(originalPrice)) return null;

  const val = parseFloat(discountValue);
  if (isNaN(val) || val <= 0) return originalPrice;

  let discount = 0;
  if (discountType === 'PERCENT') {
    discount = originalPrice * (val / 100);
    const maxAmt = parseFloat(maxDiscountAmount);
    if (maxDiscountAmount && !isNaN(maxAmt)) {
      discount = Math.min(discount, maxAmt);
    }
  } else {
    discount = val;
  }

  return Math.max(0, originalPrice - discount);
};

export default function SubscriptionLinker({ formik, subscriptionList = [], merchantList = [] }) {
  const { values, setFieldValue } = formik;
  const { linkedSubscriptions = [], merchant, discountValue, discountType, maxDiscountAmount } = values;

  const [selectedSubId, setSelectedSubId] = useState(undefined);

  // Filter subscriptions by selected merchant in form
  const availableSubscriptions = subscriptionList.filter((sub) => {
    const subMerchant = String(sub.merchant || "").trim().toLowerCase();
    const targetMerchant = String(merchant || "").trim().toLowerCase();
    const matchesMerchant = subMerchant === targetMerchant;
    
    // Ensure we only link to ACTIVE subscriptions
    const isSubActive = sub.status === "ACTIVE";
    return matchesMerchant && isSubActive;
  });

  console.log("SubscriptionLinker debug:", {
    merchant,
    subscriptionListCount: subscriptionList.length,
    availableCount: availableSubscriptions.length,
    subscriptionList
  });

  const getMerchantName = (mId) => {
    const found = merchantList.find((m) => m._id === mId || m.name === mId);
    return found ? found.name : mId;
  };

  const handleAddSubscription = () => {
    if (!selectedSubId) {
      message.warning('Please select a subscription first.');
      return;
    }

    // Check if already linked
    if (linkedSubscriptions.some((item) => item.subscriptionId === selectedSubId)) {
      message.error('This subscription is already linked.');
      return;
    }

    const newLink = {
      subscriptionId: selectedSubId,
      pricingOptionIds: [], // Empty means ALL by default
    };

    setFieldValue('linkedSubscriptions', [...linkedSubscriptions, newLink]);
    setSelectedSubId(undefined);
    message.success('Subscription linked.');
  };

  const handleRemoveSubscription = (subId) => {
    const updated = linkedSubscriptions.filter((item) => item.subscriptionId !== subId);
    setFieldValue('linkedSubscriptions', updated);
    message.info('Subscription unlinked.');
  };

  const handleToggleOption = (subId, optionIdentifier, checked) => {
    const updated = linkedSubscriptions.map((item) => {
      if (item.subscriptionId === subId) {
        let options = [...item.pricingOptionIds];
        if (checked) {
          options.push(optionIdentifier);
        } else {
          options = options.filter((id) => id !== optionIdentifier);
        }
        return { ...item, pricingOptionIds: options };
      }
      return item;
    });

    setFieldValue('linkedSubscriptions', updated);
  };

  // Build the list of active preview options for the learner preview card
  const previewItems = [];

  linkedSubscriptions.forEach((link) => {
    const sub = subscriptionList.find((s) => s._id === link.subscriptionId);
    if (!sub) return;

    const options = sub.pricingOptions || [];
    options.forEach((opt) => {
      // If linked option array is empty, include all active options. Otherwise, match by name or ID
      const identifier = opt._id || opt.name;
      const isSelected = link.pricingOptionIds.length === 0 || link.pricingOptionIds.includes(identifier);

      if (isSelected && opt.isActive !== false) {
        const originalPrice = parseFloat(opt.price);
        const hasPrice = (opt.priceType === 'PAID' || !opt.priceType) && !isNaN(originalPrice);
        const finalPrice = hasPrice 
          ? computeDiscountedPrice(originalPrice, discountValue, discountType, maxDiscountAmount)
          : null;

        previewItems.push({
          subName: sub.title,
          optName: opt.name || opt.billingCycle,
          priceType: opt.priceType,
          originalPrice,
          finalPrice,
          currency: opt.currency || 'INR',
          billingCycle: opt.billingCycle,
        });
      }
    });
  });

  return (
    <div className="subscription-linker-card">
      <div className="editor-section-title" style={{ fontSize: '14px', marginBottom: '12px' }}>
        🔗 Subscription Level Context
      </div>
      <p style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '16px' }}>
        Linking subscriptions allows learners to see computed discount previews on subscription plans when this offer is active.
      </p>

      {/* Select Box to Link a Subscription */}
      {merchant ? (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <Select
            placeholder="Select a subscription to link..."
            style={{ flex: 1, minWidth: 0 }}
            value={selectedSubId}
            onChange={setSelectedSubId}
            showSearch
            optionFilterProp="children"
            dropdownMatchSelectWidth={false}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 300 }}
          >
            {availableSubscriptions.map((sub) => (
              <Option key={sub._id} value={sub._id}>
                {sub.title} ({sub.pricingOptions?.length || 0} plans)
              </Option>
            ))}
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSubscription} style={{ flexShrink: 0 }}>
            Add Subscription
          </Button>
        </div>
      ) : (
        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '6px', padding: '8px 12px', marginBottom: '20px', fontSize: '12px', color: '#d46b08' }}>
          ⚠️ Please select a Merchant above to search and link subscriptions.
        </div>
      )}

      {/* List of Linked Subscriptions */}
      {linkedSubscriptions.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          {linkedSubscriptions.map((link) => {
            const sub = subscriptionList.find((s) => s._id === link.subscriptionId);
            if (!sub) return null;

            return (
              <div key={link.subscriptionId} className="linked-sub-row">
                <div className="linked-sub-title">
                  <span>{sub.title}</span>
                  <span style={{ fontSize: '11px', color: '#bfbfbf', fontWeight: 'normal' }}>
                    ({getMerchantName(sub.merchant)})
                  </span>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveSubscription(link.subscriptionId)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Remove
                  </Button>
                </div>

                <div className="billing-cycle-selector" style={{ marginTop: '16px', marginBottom: '16px', background: '#ffffff', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ marginBottom: '12px', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    Apply Offer to:
                  </div>
                  <Radio.Group 
                    value={link.pricingOptionIds.length === 0 ? "ALL" : "SPECIFIC"}
                    onChange={(e) => {
                      const val = e.target.value;
                      const updated = linkedSubscriptions.map((item) => {
                        if (item.subscriptionId === link.subscriptionId) {
                          if (val === 'ALL') {
                            return { ...item, pricingOptionIds: [] };
                          } else {
                            const currentOptions = sub.pricingOptions?.map(o => o._id || o.name) || [];
                            const firstOpt = currentOptions[0];
                            return { ...item, pricingOptionIds: firstOpt ? [firstOpt] : [] };
                          }
                        }
                        return item;
                      });
                      setFieldValue('linkedSubscriptions', updated);
                    }}
                  >
                    <Radio value="ALL">All Billing Cycles (Current & Future)</Radio>
                    <Radio value="SPECIFIC">Specific Billing Cycles</Radio>
                  </Radio.Group>
                </div>

                {link.pricingOptionIds.length > 0 && (
                  <div className="sub-price-grid">
                    {sub.pricingOptions?.map((opt, idx) => {
                      const identifier = opt._id || opt.name;
                      const isChecked = link.pricingOptionIds.includes(identifier);
                      const originalPrice = parseFloat(opt.price);
                      const isPaid = (opt.priceType === 'PAID' || !opt.priceType) && !isNaN(originalPrice);

                      const finalPrice = isPaid 
                        ? computeDiscountedPrice(originalPrice, discountValue, discountType, maxDiscountAmount)
                        : null;

                      return (
                        <div key={idx} className={`sub-price-option-item ${isChecked ? 'selected' : ''}`} onClick={() => handleToggleOption(link.subscriptionId, identifier, !isChecked)}>
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleToggleOption(link.subscriptionId, identifier, e.target.checked);
                            }}
                            style={{ fontWeight: 500 }}
                          >
                            {opt.name || opt.billingCycle}
                          </Checkbox>
                          <div style={{ fontSize: '12px', paddingLeft: '24px', color: '#595959' }}>
                            {opt.priceType === 'FREE' ? (
                              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>Free</span>
                            ) : opt.priceType === 'CONTACT_SALES' ? (
                              <span style={{ color: '#fa8c16', fontWeight: 'bold' }}>Contact Sales</span>
                            ) : (
                              <>
                                <span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: '6px' }}>
                                  {opt.currency || '₹'}{originalPrice.toLocaleString()}
                                </span>
                                <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
                                  {opt.currency || '₹'}{finalPrice?.toLocaleString()}
                                </span>
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  /{opt.billingCycle === 'Custom' ? `${opt.customCycleDays} Days` : opt.billingCycle}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
